import { useRef, useState } from 'react';
import {
  faCheck,
  faChevronDown,
  faHeading,
  faQuoteRight,
  faText,
} from '@awesome.me/kit-4a6bf60416/icons/classic/regular';
import { useCurrentEditor, useEditorState } from '@tiptap/react';

import { Button } from '@/components/ui/button';
import { Icon } from '@/components/ui/icon';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { applyBlockStyleChange, resetBlockWrappers } from './block-style-selection';

export type SelectorItem = {
  name: string;
  icon: typeof faText;
  command: () => void;
  isActive: boolean;
};

interface NodeSelectorProps {
  headingsOnly?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function NodeSelector({ headingsOnly = false, onOpenChange }: NodeSelectorProps = {}) {
  const { editor } = useCurrentEditor();
  const [openNode, setOpenNode] = useState(false);
  const selectionAtOpenRef = useRef<{ from: number; to: number } | null>(null);

  const editorState = useEditorState({
    editor,
    selector: (ctx) => {
      const ed = ctx.editor;
      if (!ed) {
        return {
          isParagraph: false,
          isHeading1: false,
          isHeading2: false,
          isHeading3: false,
          isHeading4: false,
          isHeading5: false,
          isHeading6: false,
          isBlockquote: false,
        };
      }

      return {
        isParagraph:
          ed.isActive('paragraph') && !ed.isActive('bulletList') && !ed.isActive('orderedList'),
        isHeading1: ed.isActive('heading', { level: 1 }),
        isHeading2: ed.isActive('heading', { level: 2 }),
        isHeading3: ed.isActive('heading', { level: 3 }),
        isHeading4: ed.isActive('heading', { level: 4 }),
        isHeading5: ed.isActive('heading', { level: 5 }),
        isHeading6: ed.isActive('heading', { level: 6 }),
        isBlockquote: ed.isActive('blockquote'),
      };
    },
  });

  if (!editor || !editorState) {
    return null;
  }

  const captureSelectionAtOpen = () => {
    if (!editor || editor.state.selection.empty) {
      selectionAtOpenRef.current = null;
      return;
    }

    selectionAtOpenRef.current = {
      from: editor.state.selection.from,
      to: editor.state.selection.to,
    };
  };

  const applyBlockStyle = (apply: () => void) => {
    applyBlockStyleChange(editor, apply, selectionAtOpenRef.current);
    selectionAtOpenRef.current = null;
  };

  const headingCommand = (level: 1 | 2 | 3 | 4 | 5 | 6) =>
    applyBlockStyle(() => {
      resetBlockWrappers(editor);
      editor.commands.setHeading({ level });
    });

  const allItems: SelectorItem[] = [
    {
      name: 'Paragraph',
      icon: faText,
      command: () => applyBlockStyle(() => resetBlockWrappers(editor)),
      isActive: editorState.isParagraph,
    },
    {
      name: 'Heading 1',
      icon: faHeading,
      command: () => headingCommand(1),
      isActive: editorState.isHeading1,
    },
    {
      name: 'Heading 2',
      icon: faHeading,
      command: () => headingCommand(2),
      isActive: editorState.isHeading2,
    },
    {
      name: 'Heading 3',
      icon: faHeading,
      command: () => headingCommand(3),
      isActive: editorState.isHeading3,
    },
    {
      name: 'Heading 4',
      icon: faHeading,
      command: () => headingCommand(4),
      isActive: editorState.isHeading4,
    },
    {
      name: 'Heading 5',
      icon: faHeading,
      command: () => headingCommand(5),
      isActive: editorState.isHeading5,
    },
    {
      name: 'Heading 6',
      icon: faHeading,
      command: () => headingCommand(6),
      isActive: editorState.isHeading6,
    },
    {
      name: 'Quote',
      icon: faQuoteRight,
      command: () =>
        applyBlockStyle(() => {
          editor.commands.focus();
          editor.commands.clearNodes();
          editor.commands.toggleBlockquote();
        }),
      isActive: editorState.isBlockquote,
    },
  ];

  const items = headingsOnly
    ? allItems.filter((item) => item.name.startsWith('Heading'))
    : allItems;

  const activeItem = items.filter((item) => item.isActive).pop() ?? {
    name: 'Multiple',
  };

  const handleOpenChange = (open: boolean) => {
    setOpenNode(open);
    onOpenChange?.(open);
  };

  return (
    <Popover open={openNode} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          size="sm"
          variant="ghost"
          colorScheme="secondary"
          className="gap-2"
          onPointerDownCapture={captureSelectionAtOpen}
        >
          <span className="whitespace-nowrap">{activeItem?.name}</span>
          <Icon icon={faChevronDown} />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        data-rich-text-editor-ui="format-toolbar-submenu"
        sideOffset={5}
        align="start"
        className="w-32 p-1"
      >
        {items.map((item) => (
          <Button
            className="w-full justify-between px-3"
            variant="ghost"
            colorScheme="secondary"
            key={item.name}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              item.command();
              handleOpenChange(false);
            }}
          >
            <span>{item.name}</span>
            {activeItem.name === item.name && <Icon icon={faCheck} />}
          </Button>
        ))}
      </PopoverContent>
    </Popover>
  );
}
